import { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, RotateCcw, AlertCircle } from 'lucide-react';
import { Button, CodeEditor, OutputPanel, LoadingSpinner, ExecutionPlanModal } from '../components';
import { usePyodide } from '../hooks/usePyodide';

const RESTRICTED_FUNCTIONS = [
  'Web.Contents',
  'Web.BrowserContents',
  'Sql.Database',
  'Sql.Databases',
  'File.Contents',
  'Folder.Contents',
  'Folder.Files',
  'Excel.Workbook',
  'Csv.Document',
  'SharePoint.Contents',
  'SharePoint.Files',
  'SharePoint.Tables',
  'OData.Feed',
  'Odbc.DataSource',
  'Odbc.Query',
  'OleDb.DataSource',
  'OleDb.Query',
  'Oracle.Database',
  'PostgreSQL.Database',
  'MySQL.Database',
  'Teradata.Database',
  'AzureStorage.Blobs',
  'AzureStorage.Tables',
  'AmazonRedshift.Database',
  'GoogleBigQuery.Database',
  'Snowflake.Databases',
  'Databricks.Catalogs',
];

const DEFAULT_CODE = `// M Language Sandbox
// Write and test your M code here

let
    numbers = {1, 2, 3, 4, 5},
    doubled = List.Transform(numbers, each _ * 2),
    sum = List.Sum(doubled)
in
    sum`;

export default function Sandbox() {
  const { runMCodeWithTrace, isReady, isLoading: pyodideLoading, loadingStatus } = usePyodide();
  const [code, setCode] = useState(DEFAULT_CODE);
  const [result, setResult] = useState(null);
  const [structured, setStructured] = useState(null);
  const [error, setError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [traceData, setTraceData] = useState(null);
  const [showExecutionPlan, setShowExecutionPlan] = useState(false);

  const codeRef = useRef(code);

  const handleCodeChange = useCallback((newCode) => {
    codeRef.current = newCode;
    setCode(newCode);
  }, []);

  const handleRun = useCallback(async (passedCode) => {
    if (!isReady || isRunning) return;

    const currentCode = (typeof passedCode === 'string') ? passedCode : codeRef.current;
    const trimmedCode = currentCode.trim();

    if (!trimmedCode) {
      setError('Please enter some code first');
      return;
    }

    // Check for restricted functions
    for (const fn of RESTRICTED_FUNCTIONS) {
      if (trimmedCode.includes(fn)) {
        setError(`Function "${fn}" is not available in the sandbox. Data connectors are restricted.`);
        return;
      }
    }

    setIsRunning(true);
    setResult(null);
    setStructured(null);
    setError(null);
    setTraceData(null);

    try {
      const response = await runMCodeWithTrace(trimmedCode);

      if (response.trace) {
        setTraceData(response.trace);
      }

      if (response.success) {
        setResult(response.result);
        setStructured(response.structured || null);
      } else {
        setError(response.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRunning(false);
    }
  }, [isReady, isRunning, runMCodeWithTrace]);

  const handleReset = () => {
    codeRef.current = DEFAULT_CODE;
    setCode(DEFAULT_CODE);
    setResult(null);
    setStructured(null);
    setError(null);
  };

  if (pyodideLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner message={loadingStatus || "Loading Python runtime..."} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="p-4 border-b border-[#2a2d31] flex-shrink-0">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm" pill={false}>
                <ArrowLeft size={18} />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold text-game-text">M Playground</h1>
              <p className="text-sm text-game-text-dim">Test your M code freely</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-6xl mx-auto w-full p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: Code editor */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            {/* Code editor */}
            <div className="bg-game-surface rounded-2xl border border-[#2a2d31] overflow-hidden">
              <div className="flex items-center justify-between px-4 h-[46px] bg-[#2a2d31]/50 border-b border-[#2a2d31]">
                <span className="text-sm font-semibold text-game-text">Your Code</span>
                <button
                  onClick={handleReset}
                  className="flex items-center text-xs text-game-text-dim hover:text-game-text transition-colors"
                >
                  <RotateCcw size={12} className="mr-1" />
                  Reset
                </button>
              </div>
              <CodeEditor
                value={code}
                onChange={handleCodeChange}
                onRun={handleRun}
                height="400px"
              />
            </div>

            {/* Run button */}
            <Button
              onClick={handleRun}
              disabled={!isReady || isRunning}
              className="w-full"
            >
              <Play size={18} className="mr-2" />
              {isRunning ? 'Running...' : 'Run Code'}
            </Button>

            {/* Output */}
            <OutputPanel
              result={result}
              structured={structured}
              error={error}
              isRunning={isRunning}
              onShowExecutionPlan={traceData ? () => setShowExecutionPlan(true) : null}
            />
          </div>

          {/* Right column: Rules */}
          <div className="space-y-4">
            {/* Rules */}
            <div className="p-5 bg-game-surface rounded-2xl border border-[#2a2d31]">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="text-game-warning" size={20} />
                <h2 className="font-bold text-game-text">Playground Rules</h2>
              </div>

              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold text-game-text mb-2">What you can do:</h3>
                  <ul className="space-y-1 text-game-text-dim">
                    <li className="flex items-start gap-2">
                      <span className="text-game-primary mt-0.5">&#10003;</span>
                      <span>Write and test M expressions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-game-primary mt-0.5">&#10003;</span>
                      <span>Use 200+ built-in M functions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-game-primary mt-0.5">&#10003;</span>
                      <span>Create lists, records, tables</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-game-primary mt-0.5">&#10003;</span>
                      <span>Define custom functions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-game-primary mt-0.5">&#10003;</span>
                      <span>Parse JSON, CSV, XML data</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-game-text mb-2">Restricted functions:</h3>
                  <p className="text-game-text-dim mb-2">
                    Data connector functions are not available in this playground:
                  </p>
                  <div className="bg-[#2a2d31] rounded-xl p-3 max-h-48 overflow-y-auto scrollbar-contrast">
                    <ul className="space-y-1 text-xs font-mono text-game-text-dim">
                      {RESTRICTED_FUNCTIONS.map((fn) => (
                        <li key={fn} className="flex items-center gap-2">
                          <span className="text-red-400">&#10007;</span>
                          <span>{fn}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="pt-2 border-t border-[#2a2d31]">
                  <p className="text-game-text-dim text-xs">
                    This sandbox runs entirely in your browser using a Python-based M interpreter.
                    No data leaves your device.
                  </p>
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="p-5 bg-game-surface rounded-2xl border border-[#2a2d31]">
              <h2 className="font-bold text-game-text mb-3">Tips</h2>
              <ul className="space-y-2 text-sm text-game-text-dim">
                <li className="flex items-start gap-2">
                  <span className="text-game-accent font-bold">1.</span>
                  <span>Press <kbd className="px-1.5 py-0.5 bg-[#2a2d31] rounded text-xs font-mono">Cmd+Enter</kbd> to run code</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-game-accent font-bold">2.</span>
                  <span>Use <code className="text-game-primary font-mono">let...in</code> for multi-step expressions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-game-accent font-bold">3.</span>
                  <span>Define inline data to test table operations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      {/* Execution Plan modal */}
      <ExecutionPlanModal
        isOpen={showExecutionPlan}
        onClose={() => setShowExecutionPlan(false)}
        trace={traceData}
        isLoading={false}
      />
    </div>
  );
}
